import React, { useState } from "react";
import { Shield, CheckCircle2, Clock, ArrowRight, Lock, Check } from "lucide-react";
import NdaModal from "./NdaModal";
import NdaDownloadButton from "./NdaDownloadButton";
import { NdaPdfSource } from "../../utils/hooks/scoping/useNdaPdfUrl";
import { useNavigate } from "react-router-dom";

interface NdaGateScreenProps {
  token: string;
  clientName: string;
  ndaText: string;
  alreadySigned?: boolean;
  signerName?: string | null;
  signedAt?: string | null;
  ndaSource?: NdaPdfSource | null;
  onSigned: () => void;
  onContinue?: () => void;
}

/**
 * Full-page NDA gate screen — hero layout matching design spec.
 */
const NdaGateScreen: React.FC<NdaGateScreenProps> = ({
  token,
  clientName,
  ndaText,
  alreadySigned = false,
  signerName,
  signedAt,
  ndaSource,
  onSigned,
  onContinue
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleSigned = () => {
    setIsModalOpen(false);
    onSigned();
  };

  const signedDateDisplay = signedAt
    ? new Date(signedAt).toLocaleString("en-NZ", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
    : null;

  return (
    <div className="min-h-[calc(100vh-80px)] w-full flex flex-col items-start justify-start bg-[#f4f6fb] px-0 py-10 montserrat-font">

      {/* ── Hero section ─────────────────────────────────────────────── */}
      <div className="w-full flex flex-row items-center justify-between mb-8 gap-6">
        {/* Left: headline + subtitle */}
        <div className="flex-1 min-w-0">
          <h1 className="text-[2.3rem] font-black text-[#0b1739] leading-[1.3] tracking-tight mb-3">
            Secure, Simple and<br />Confidential.
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed max-w-[600px]">
            Before you begin the scoping questionnaire, please review and sign
            our Non-Disclosure Agreement. Your information is protected every
            step of the way.
          </p>
          <span className="inline-flex items-center gap-1.5 mt-4 text-[11px] font-semibold text-[#58CC02]">
            <Lock size={11} />
            Secure intake session
          </span>
        </div>

        <div className="shrink-0 hidden sm:flex items-center justify-center w-64 h-48">
          <img src="/NDA-ICON.png" alt="NDA Icon" className="w-full h-full object-contain" />
        </div>
      </div>

      {/* ── NDA Card ─────────────────────────────────────────────────── */}
      <div className="w-full bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">

        {/* Card header row */}
        <div className="px-6 pt-5 pb-4 flex items-start gap-4 border-b border-slate-200/60">
          <div className="w-11 h-11 rounded-xl bg-[#0b1739] flex items-center justify-center shrink-0">
            <Shield size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-[15px] font-black text-[#0b1739] leading-tight">
                Non-Disclosure Agreement Required
              </h2>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-500 tracking-wide uppercase">
                Legal
              </span>
            </div>
            <p className="text-[12.5px] text-slate-500 mt-1 leading-relaxed">
              Review and digitally sign our NDA to access the scoping questionnaire. This ensures the
              confidentiality and security of your sensitive information.
            </p>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">

          {alreadySigned ? (
            <>
              {/* Already signed */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <CheckCircle2 size={20} className="text-emerald-500 shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <p className="text-sm font-bold text-emerald-800">NDA Signed</p>
                  {signerName && (
                    <p className="text-xs text-emerald-700">
                      Signed by: <strong>{signerName}</strong>
                    </p>
                  )}
                  {signedDateDisplay && (
                    <p className="text-[11px] text-emerald-600 flex items-center gap-1">
                      <Clock size={10} />
                      {signedDateDisplay}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <NdaDownloadButton
                  ndaSource={ndaSource}
                  className="flex-1 inline-flex items-center justify-center gap-2 h-10
                             rounded-xl border border-[#1b3a8c]/30 bg-[#1b3a8c]/5
                             text-[#1b3a8c] text-sm font-semibold hover:bg-[#1b3a8c]/10 transition
                             disabled:opacity-50 disabled:pointer-events-none"
                />
                {onContinue && (
                  <button
                    type="button"
                    onClick={onContinue}
                    className="flex-1 inline-flex items-center justify-center gap-2 h-10
                               rounded-xl bg-[#0b1739] text-white text-sm font-bold
                               hover:bg-[#0b1739]/90 transition"
                  >
                    Continue to Form
                    <ArrowRight size={14} />
                  </button>
                )}
              </div>

              <p className="text-[10px] text-slate-400 text-center">
                Your signature has been securely recorded on our servers.
              </p>
            </>
          ) : (
            <>
              {/* Bullet points */}
              <div className="space-y-3">
                {[
                  "Protects confidential security findings and assessment data",
                  "Required by Capture The Bug's standard assessment policy",
                  "Takes less than 2 minutes to complete"
                ].map((point) => (
                  <div key={point} className="flex items-center gap-3 text-[13px] text-slate-600">
                    <div className="w-5 h-5 rounded-full bg-[#edf7ed] flex items-center justify-center shrink-0">
                      <Check size={10} className="text-[#58CC02]" strokeWidth={4.5} />
                    </div>
                    {point}
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="w-full inline-flex items-center justify-center gap-2 h-12
                           rounded-xl bg-[#0b1739] text-white text-[14px] font-bold
                           hover:bg-[#0b1739]/90 transition active:scale-[0.98]"
              >
                Review & Sign NDA
                <ArrowRight size={15} />
              </button>

              {/* Login link */}
              <p className="text-[12.5px] text-slate-500 text-center">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => navigate(`/login?ref=intake&token=${token}`)}
                  className="font-bold text-[#0b1739] hover:underline"
                >
                  Log in
                </button>
              </p>
            </>
          )}
        </div>
      </div>

      {/* ── Bottom trust note ────────────────────────────────────────── */}
      <div className="w-full mt-4 bg-white border border-slate-200/80 rounded-xl p-4 flex items-center gap-3">
        <Lock size={16} className="text-slate-800 shrink-0" />
        <div className="flex-1 min-w-0 text-left">
          <p className="text-xs font-semibold text-slate-800">
            Your signature is securely encrypted and stored on our servers.
          </p>
          <p className="text-[11px] text-slate-400">
            We never share your information with third parties.
          </p>
        </div>
      </div>

      {/* NDA Modal */}
      <NdaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSigned={handleSigned}
        token={token}
        clientName={clientName}
        ndaText={ndaText}
        id={ndaSource?.id || undefined}
      />
    </div>
  );
};

export default NdaGateScreen;
